import { useState } from 'react';
import dayjs from 'dayjs';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type {
  Category,
  DetailActionType,
  EngineTypeKey,
  Machine,
  MachineOption,
  MachineUpdate,
  StatusKey,
  Supplier,
} from '../types';
import { ENGINE_TYPES, formatSpecificationLabel, SPECIFICATION_DEFINITIONS, STATUSES } from '../constants';
import ActionDialog from '../../../components/ActionDialog';
import { DETAIL_ACTION_LABELS as LABELS } from '../../../utils/labels';
import SupplierModal from '../../settings/components/suppliers/SupplierModal';
import CategoryModal from '../../settings/components/categories/CategoryModal';
import OptionModal from '../../settings/components/options/OptionModal';
import CategoryAutocompleteField from './CategoryAutocompleteField';
import OptionsAutocompleteField from './OptionsAutocompleteField';
import { useCategoryLinkedOptions } from '../hooks/useCategoryLinkedOptions';
import { useSpecificationsForm } from '../hooks/useSpecificationsForm';
import { resolveNamesByIds } from '../../../utils/resolveNamesByIds';

const CREATE_SENTINEL = '__create__';

interface DetailActionDialogProps {
  action: DetailActionType;
  machine: Machine;
  suppliers: Supplier[];
  categories: Category[];
  options: MachineOption[];
  saving: boolean;
  onClose: () => void;
  onSave: (updates: MachineUpdate) => void;
  onAddSupplier: (name: string, email: string) => Promise<Supplier | null>;
  onAddCategory: (name: string) => Promise<Category | null>;
  onAddOption: (name: string) => Promise<MachineOption | null>;
}

export default function DetailActionDialog({
  action,
  machine,
  suppliers,
  categories,
  options: optionsCatalog,
  saving,
  onClose,
  onSave,
  onAddSupplier,
  onAddCategory,
  onAddOption,
}: DetailActionDialogProps) {
  const title = {
    status: LABELS.titleStatus,
    machine_details: LABELS.titleMachineDetails,
    machine_number: LABELS.titleMachineNumber,
    period: LABELS.titlePeriod,
  }[action];

  const [quickCreate, setQuickCreate] = useState<'supplier' | 'category' | 'option' | null>(null);

  // Newly quick-created records, merged with the prop-provided lists so a freshly created
  // record is selectable/visible immediately, without waiting for the parent to re-fetch.
  const [extraSuppliers, setExtraSuppliers] = useState<Supplier[]>([]);
  const [extraCategories, setExtraCategories] = useState<Category[]>([]);
  const [extraOptions, setExtraOptions] = useState<MachineOption[]>([]);

  const allSuppliers = [...suppliers, ...extraSuppliers];
  const allCategories = [...categories, ...extraCategories];
  const allOptions = [...optionsCatalog, ...extraOptions];

  // Status
  const [status, setStatus] = useState<string>(machine.status);

  // Machine details
  const [name, setName] = useState(machine.name);
  const [nameError, setNameError] = useState('');
  const linkedOptionNames = new Set(resolveNamesByIds(machine.option_ids ?? [], allOptions));
  const [category, setCategory] = useState(machine.category ?? '');
  const [categoryId, setCategoryId] = useState<string | null>(machine.category_id ?? null);
  const [engineType, setEngineType] = useState<EngineTypeKey>(machine.engine_type ?? 'none');
  const [notes, setNotes] = useState(machine.notes ?? '');
  const [freeTextOptions, setFreeTextOptions] = useState<string[]>(
    (machine.options ?? []).filter((name) => !linkedOptionNames.has(name))
  );
  const [optionIds, setOptionIds] = useState<string[]>(machine.option_ids ?? []);
  const [supplierId, setSupplierId] = useState(machine.supplier_id ?? '');
  const specsForm = useSpecificationsForm(machine.specifications);

  const {
    categoryValue,
    categoryInputValue,
    filteredCatalogOptions,
    hasNoLinkedOptions,
    eligibleCategories,
    hasNoEligibleCategories,
    categorySpecs,
    sortedCategorySpecKeys,
  } = useCategoryLinkedOptions({
    categories: allCategories,
    options: allOptions,
    category,
    categoryId,
    selectedOptionIds: optionIds,
  });

  const selectedOptionsValue: (MachineOption | string)[] = [
    ...allOptions.filter((o) => optionIds.includes(o.id)),
    ...freeTextOptions,
  ];

  // Machine number
  const [machineNumber, setMachineNumber] = useState(machine.machine_number ?? '');

  // Period
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    machine.start_date ? dayjs(machine.start_date) : null
  );
  const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(
    machine.start_time ? dayjs(`2000-01-01T${machine.start_time}`) : null
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    machine.end_date ? dayjs(machine.end_date) : null
  );
  const [endTime, setEndTime] = useState<dayjs.Dayjs | null>(
    machine.end_time ? dayjs(`2000-01-01T${machine.end_time}`) : null
  );
  const [startDateError, setStartDateError] = useState('');
  const [startTimeError, setStartTimeError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [endTimeError, setEndTimeError] = useState('');

  function handleStartDateChange(v: dayjs.Dayjs | null) {
    setStartDate(v);
    if (startDateError) setStartDateError('');
    const sd = v?.format('YYYY-MM-DD');
    const ed = endDate?.format('YYYY-MM-DD');
    if (sd && ed && sd === ed && startTime && endTime && endTime.format('HH:mm') <= startTime.format('HH:mm')) {
      setEndTime(null);
    }
  }

  function handleStartTimeChange(v: dayjs.Dayjs | null) {
    setStartTime(v);
    if (startTimeError) setStartTimeError('');
    const sd = startDate?.format('YYYY-MM-DD');
    const ed = endDate?.format('YYYY-MM-DD');
    if (sd && ed && sd === ed && v && endTime && endTime.format('HH:mm') <= v.format('HH:mm')) {
      setEndTime(null);
    }
  }

  function handleEndDateChange(v: dayjs.Dayjs | null) {
    setEndDate(v);
    if (endDateError) setEndDateError('');
    const sd = startDate?.format('YYYY-MM-DD');
    const ed = v?.format('YYYY-MM-DD');
    if (sd && ed && ed === sd && startTime && endTime && startTime.format('HH:mm') >= endTime.format('HH:mm')) {
      setStartTime(null);
    }
  }

  function handleEndTimeChange(v: dayjs.Dayjs | null) {
    setEndTime(v);
    if (endTimeError) setEndTimeError('');
    const sd = startDate?.format('YYYY-MM-DD');
    const ed = endDate?.format('YYYY-MM-DD');
    if (sd && ed && sd === ed && startTime && v && startTime.format('HH:mm') >= v.format('HH:mm')) {
      setStartTime(null);
    }
  }

  const startTimeMax =
    startDate && endDate && startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD') && endTime
      ? endTime.subtract(30, 'minute')
      : undefined;

  const endTimeMin =
    startDate && endDate && startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD') && startTime
      ? startTime.add(30, 'minute')
      : undefined;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (action === 'status') {
      onSave({ status: status as StatusKey });
      return;
    }

    if (action === 'machine_details') {
      const trimmedName = name.trim();
      if (!trimmedName) { setNameError(LABELS.nameRequired); return; }
      const specsValid = specsForm.validateSpecifications(categorySpecs, sortedCategorySpecKeys, LABELS.specificationRequired);
      if (!specsValid) return;
      const specificationsPayload = specsForm.buildSpecificationsPayload(sortedCategorySpecKeys);
      onSave({
        name: trimmedName,
        category: category.trim() || undefined,
        category_id: categoryId,
        engine_type: engineType,
        options: freeTextOptions,
        option_ids: optionIds,
        specifications: specificationsPayload,
        notes: notes.trim() || undefined,
        supplier_id: supplierId || null,
      });
      return;
    }

    if (action === 'machine_number') {
      onSave({ machine_number: machineNumber.trim() || undefined });
      return;
    }

    if (action === 'period') {
      let valid = true;
      if (!startDate) { setStartDateError(LABELS.startDateRequired); valid = false; }
      if (!startTime) { setStartTimeError(LABELS.startTimeRequired); valid = false; }
      if (!endDate) { setEndDateError(LABELS.endDateRequired); valid = false; }
      if (!endTime) { setEndTimeError(LABELS.endTimeRequired); valid = false; }
      if (!valid) return;

      const sd = startDate!.format('YYYY-MM-DD');
      const st = startTime!.format('HH:mm');
      const ed = endDate!.format('YYYY-MM-DD');
      const et = endTime!.format('HH:mm');

      if (ed < sd) { setEndDateError(LABELS.endDateAfterStart); return; }
      if (ed === sd && et <= st) { setEndTimeError(LABELS.endTimeAfterStart); return; }

      onSave({ start_date: sd, start_time: st, end_date: ed, end_time: et });
    }
  }

  return (
    <>
    <ActionDialog
      title={title}
      formId="detail-action-form"
      saving={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
      saveLabel={LABELS.save}
      cancelLabel={LABELS.cancel}
      testId="detail-action-dialog"
    >
      {action === 'status' && (
        <FormControl fullWidth>
          <InputLabel id="detail-status-label">{LABELS.statusLabel}</InputLabel>
          <Select
            labelId="detail-status-label"
            label={LABELS.statusLabel}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            inputProps={{ 'aria-label': LABELS.statusLabel }}
          >
            {Object.entries(STATUSES).map(([key, { label }]) => (
              <MenuItem key={key} value={key} data-testid={`option-status-${key}`}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {action === 'machine_details' && (
        <div className="flex flex-col gap-4">
          <TextField
            fullWidth
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            error={!!nameError}
            helperText={nameError || undefined}
            required
            slotProps={{ htmlInput: { 'aria-label': LABELS.nameLabel } }}
          />
          <CategoryAutocompleteField
            categories={eligibleCategories}
            value={categoryValue}
            inputValue={categoryInputValue}
            hasNoEligibleCategories={hasNoEligibleCategories}
            label={LABELS.categoryLabel}
            addCategoryLabel={LABELS.addCategoryOption}
            noEligibleCategoriesLabel={LABELS.noLinkedCategories}
            onSelectCategory={(cat) => {
              setCategory(cat.name);
              setCategoryId(cat.id);
              specsForm.clearStaleSpecifications((cat.specifications ?? []).map((s) => s.key));
            }}
            onFreeTextChange={(text) => {
              setCategory(text);
              setCategoryId(null);
              specsForm.clearStaleSpecifications([]);
            }}
            onCreateNew={() => setQuickCreate('category')}
          />
          <FormControl fullWidth>
            <InputLabel id="detail-engine-type-label">{LABELS.engineTypeLabel}</InputLabel>
            <Select
              labelId="detail-engine-type-label"
              label={LABELS.engineTypeLabel}
              value={engineType}
              onChange={(e) => setEngineType(e.target.value as EngineTypeKey)}
              inputProps={{ 'aria-label': LABELS.engineTypeLabel }}
            >
              {Object.entries(ENGINE_TYPES).map(([key, { label }]) => (
                <MenuItem key={key} value={key} data-testid={`option-engine-${key}`}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {sortedCategorySpecKeys.map((k) => {
            const spec = categorySpecs.find((s) => s.key === k);
            return (
              <TextField
                key={k}
                fullWidth
                type="number"
                label={formatSpecificationLabel(k)}
                required={spec?.required}
                value={specsForm.specifications[k] ?? ''}
                onChange={(e) => specsForm.setSpecificationValue(k, e.target.value)}
                error={!!specsForm.specErrors[k]}
                helperText={specsForm.specErrors[k]}
                slotProps={{
                  htmlInput: { step: 10 ** -SPECIFICATION_DEFINITIONS[k].decimals, 'data-testid': `spec-input-${k}` },
                }}
              />
            );
          })}
          <OptionsAutocompleteField
            options={filteredCatalogOptions}
            value={selectedOptionsValue}
            hasNoLinkedOptions={hasNoLinkedOptions}
            label={LABELS.optionsLabel}
            addOptionLabel={LABELS.addOptionOption}
            noLinkedOptionsLabel={LABELS.noLinkedOptions}
            placeholder={LABELS.optionsPlaceholder}
            onChange={(optionIds, freeTextOptions) => {
              setOptionIds(optionIds);
              setFreeTextOptions(freeTextOptions);
            }}
            onCreateNew={() => setQuickCreate('option')}
          />
          <FormControl fullWidth>
            <InputLabel id="detail-supplier-label">{LABELS.supplierLabel}</InputLabel>
            <Select
              labelId="detail-supplier-label"
              label={LABELS.supplierLabel}
              value={supplierId}
              onChange={(e) => {
                if (e.target.value === CREATE_SENTINEL) {
                  setQuickCreate('supplier');
                  return;
                }
                setSupplierId(e.target.value);
              }}
              inputProps={{ 'aria-label': LABELS.supplierLabel }}
            >
              <MenuItem value={CREATE_SENTINEL} data-testid="option-add-supplier" className="text-primary">
                <AddIcon fontSize="small" className="mr-1" />
                {LABELS.addSupplierOption}
              </MenuItem>
              <MenuItem value="">{LABELS.noSupplier}</MenuItem>
              {allSuppliers
                .filter((s) => !s.archived)
                .map((s) => (
                  <MenuItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>
                    {s.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={LABELS.notesLabel}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': LABELS.notesLabel } }}
          />
        </div>
      )}

      {action === 'machine_number' && (
        <TextField
          fullWidth
          label={LABELS.machineNumberLabel}
          value={machineNumber}
          onChange={(e) => setMachineNumber(e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': LABELS.machineNumberLabel } }}
        />
      )}

      {action === 'period' && (
        <div className="grid grid-cols-2 gap-3">
          <DatePicker
            label={LABELS.startDateLabel}
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={endDate ?? undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!startDateError,
                helperText: startDateError || undefined,
              },
            }}
          />
          <TimePicker
            label={LABELS.startTimeLabel}
            ampm={false}
            minutesStep={30}
            timeSteps={{ minutes: 30 }}
            value={startTime}
            onChange={handleStartTimeChange}
            maxTime={startTimeMax}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!startTimeError,
                helperText: startTimeError || undefined,
              },
            }}
          />
          <DatePicker
            label={LABELS.endDateLabel}
            value={endDate}
            onChange={handleEndDateChange}
            minDate={startDate ?? undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!endDateError,
                helperText: endDateError || undefined,
              },
            }}
          />
          <TimePicker
            label={LABELS.endTimeLabel}
            ampm={false}
            minutesStep={30}
            timeSteps={{ minutes: 30 }}
            value={endTime}
            onChange={handleEndTimeChange}
            minTime={endTimeMin}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!endTimeError,
                helperText: endTimeError || undefined,
              },
            }}
          />
        </div>
      )}
    </ActionDialog>
    {quickCreate === 'supplier' && (
      <SupplierModal
        onClose={() => setQuickCreate(null)}
        onSave={async (supplierName, email) => {
          const created = await onAddSupplier(supplierName, email);
          if (!created) return false;
          setExtraSuppliers((prev) => [...prev, created]);
          setSupplierId(created.id);
          return true;
        }}
      />
    )}
    {quickCreate === 'category' && (
      <CategoryModal
        existingCategories={allCategories}
        onClose={() => setQuickCreate(null)}
        onSave={async (categoryName) => {
          const created = await onAddCategory(categoryName);
          if (!created) return false;
          setExtraCategories((prev) => [...prev, created]);
          setCategory(created.name);
          setCategoryId(created.id);
          return true;
        }}
      />
    )}
    {quickCreate === 'option' && (
      <OptionModal
        existingOptions={allOptions}
        onClose={() => setQuickCreate(null)}
        onSave={async (optionName) => {
          const created = await onAddOption(optionName);
          if (!created) return false;
          setExtraOptions((prev) => [...prev, created]);
          setOptionIds((prev) => [...prev, created.id]);
          return true;
        }}
      />
    )}
    </>
  );
}
